import { useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Check, Copy, CheckCircle2, Loader2, AlertCircle, Brain, Sparkles, Zap, Bot } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { copyFormattedContent, renderMarkdownToHtml } from "@/utils/markdownUtils";
import { CitationsDisplay } from "@/components/CitationsDisplay";

interface ModelResponseData {
  model: string;
  modelDisplayName: string;
  content: string;
  tokensUsed?: {
    input: number;
    output: number;
    total: number;
  };
  costInCredits?: number;
  error?: string;
  status: "pending" | "streaming" | "done" | "error";
  citations?: Array<{
    type: "url_citation";
    url_citation: {
      url: string;
      title?: string;
      content?: string;
      start_index?: number;
      end_index?: number;
    };
  }>;
  images?: Array<{ url: string; detail?: string }>;
}

interface MultiModelResponseViewProps {
  responses: ModelResponseData[];
  onSelectResponse?: (model: string, content: string) => void;
  isStreaming?: boolean;
}

function getModelIcon(modelId: string) {
  if (modelId.includes("gpt") || modelId.includes("openai")) {
    return <Sparkles className="h-4 w-4 text-emerald-500" />;
  }
  if (modelId.includes("claude") || modelId.includes("anthropic")) {
    return <Brain className="h-4 w-4 text-orange-500" />;
  }
  if (modelId.includes("gemini") || modelId.includes("google")) {
    return <Zap className="h-4 w-4 text-blue-500" />;
  }
  if (modelId.includes("grok")) {
    return <Bot className="h-4 w-4 text-purple-500" />;
  }
  if (modelId.includes("llama")) {
    return <Bot className="h-4 w-4 text-indigo-500" />;
  }
  if (modelId.includes("mistral")) {
    return <Sparkles className="h-4 w-4 text-cyan-500" />;
  }
  return <Bot className="h-4 w-4 text-muted-foreground" />;
}

function ResponseCard({
  response,
  onSelect,
  onCopy,
}: {
  response: ModelResponseData;
  onSelect?: () => void;
  onCopy?: () => void;
}) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await copyFormattedContent(response.content);
      setCopied(true);
      toast({
        title: "Copied",
        description: `Response from ${response.modelDisplayName} copied to clipboard`,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      try {
        await navigator.clipboard.writeText(response.content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch { /* ignore fallback failure */ }
      toast({
        title: "Copied",
        description: `Response from ${response.modelDisplayName} copied to clipboard`,
      });
    }
  };

  return (
    <Card
      className={cn(
        "flex flex-col h-full",
        response.status === "error" && "border-destructive/50"
      )}
      data-testid={`card-response-${response.model.replace("/", "-")}`}
    >
      <CardHeader className="pb-2 flex-none">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {getModelIcon(response.model)}
            <span className="font-medium text-sm truncate">
              {response.modelDisplayName}
            </span>
            {response.status === "streaming" && (
              <Loader2 className="h-3 w-3 animate-spin text-primary" />
            )}
            {response.status === "done" && (
              <CheckCircle2 className="h-3 w-3 text-green-500" />
            )}
            {response.status === "error" && (
              <AlertCircle className="h-3 w-3 text-destructive" />
            )}
          </div>
          {response.costInCredits !== undefined && response.status === "done" && (
            <Badge variant="secondary" className="text-xs flex-none">
              {response.costInCredits} credits
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <ScrollArea className="flex-1 pr-2">
          {response.status === "pending" ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ) : response.status === "error" ? (
            <div className="flex items-center gap-2 text-destructive text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{response.error || "An error occurred"}</span>
            </div>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
              <div
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownToHtml(response.content),
                }}
              />
              {response.status === "streaming" && (
                <span className="inline-block w-1 h-4 bg-primary animate-pulse ml-0.5" />
              )}
            </div>
          )}
        </ScrollArea>

        {response.status === "done" && response.content && (
          <>
            {response.citations && response.citations.length > 0 && (
              <div className="mt-3">
                <CitationsDisplay citations={response.citations as any} />
              </div>
            )}
            {response.images && response.images.length > 0 && (
              <div className="mt-3 space-y-2">
                {response.images.map((image, idx) => (
                  <div key={idx} className="rounded-lg overflow-hidden border">
                    <img
                      src={image.url}
                      alt={`Generated image ${idx + 1}`}
                      className="w-full h-auto max-w-2xl"
                      loading="lazy"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3EImage Load Error%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between gap-2 pt-3 mt-3 border-t">
              <div className="text-xs text-muted-foreground">
                {response.tokensUsed && (
                  <span>{response.tokensUsed.total} tokens</span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  data-testid={`button-copy-${response.model.replace("/", "-")}`}
                >
                  {copied ? (
                    <Check className="h-3 w-3 mr-1" />
                  ) : (
                    <Copy className="h-3 w-3 mr-1" />
                  )}
                  Copy
                </Button>
                {onSelect && (
                  <Button
                    variant="default"
                    size="sm"
                    onClick={onSelect}
                    data-testid={`button-select-${response.model.replace("/", "-")}`}
                  >
                    <Check className="h-3 w-3 mr-1" />
                    Use This
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function MultiModelResponseView({
  responses,
  onSelectResponse,
  isStreaming = false,
}: MultiModelResponseViewProps) {
  const gridCols =
    responses.length === 1
      ? "grid-cols-1"
      : responses.length === 2
      ? "grid-cols-1 md:grid-cols-2"
      : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3";

  const totalCredits = responses.reduce(
    (sum, r) => sum + (r.costInCredits || 0),
    0
  );

  const allDone = responses.every(
    (r) => r.status === "done" || r.status === "error"
  );

  return (
    <div className="space-y-4" data-testid="multi-model-response-view">
      {responses.length > 1 && allDone && (
        <div className="flex items-center justify-between px-1 text-sm">
          <span className="text-muted-foreground">
            Comparing {responses.length} models
          </span>
          <Badge variant="outline" data-testid="badge-total-spent">
            Total: {totalCredits} credits
          </Badge>
        </div>
      )}

      <div className={cn("grid gap-4", gridCols)}>
        {responses.map((response) => (
          <ResponseCard
            key={response.model}
            response={response}
            onSelect={
              onSelectResponse && response.status === "done" && response.content
                ? () => onSelectResponse(response.model, response.content)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

export type { ModelResponseData };
